import { defineComponent as _defineComponent } from 'vue'
import { reactive, toRefs, provide, nextTick } from "vue";
import { isValidCommunityTalkStoreKey, useCommunityTalkStore } from "@/store/communityTalk/communityTalkStore";
import { TalkRoomViewModel } from "@/viewModels/communityTalk/talkRoomViewModel";
import TalkRoomController from "@/controllers/communityTalk/talkRoomController";
import { ApiGetLoadAddStatus, ApiGetLoadAddStatusType, ApiGetLoadStatus, ApiGetLoadStatusType } from "@/modules/types/apiLoadStatusType";
import ApiGetLoadMaster from "@/components/common/api/ApiGetLoadMaster.vue";
import ApiGetLoadWithAddMaster from "@/components/common/api/ApiGetLoadWithAddMaster.vue";
import HeaderPart from "@/components/common/HeaderPart.vue";
import FooterPart from "@/components/common/FooterPart.vue";
import ReviCoLogo from "@/components/common/ReviCoLogo.vue";
import CommunityTalkTitle from "@/components/communityTalk/CommunityTalkTitle.vue";
import TalkRoomCount from "@/components/communityTalk/talkRoom/TalkRoomCount.vue";
import TalkRoomList from "@/components/communityTalk//talkRoom/TalkRoomList.vue";
import TalkRoomMoreButton from "@/components/communityTalk//talkRoom/TalkRoomMoreButton.vue";
import NoTalkRoom from "@/components/communityTalk//talkRoom/NoTalkRoom.vue";
import TalkRoomPost from "@/components/communityTalk//talkRoom/TalkRoomPost.vue";
import { LayoutTagGetResponse_CustomHtmlCss, LayoutTagGetResponse_WordSetting } from "@/apiRequests/models/communityTalk/layoutTagGetResponse";
import CustomCssUtil from "@/modules/utils/customCssUtil";

interface State {
	/**
	 * トークルーム情報。
	 */
	talkRoomViewModel: TalkRoomViewModel | null;
	/**
	 * 表示タグ取得時のAPIステータス。
	 */
	layoutTagGetStatus: ApiGetLoadStatusType;
	/**
	 * トークルーム取得時のAPIステータス。
	 */
	talkRoomGetStatus: ApiGetLoadAddStatusType;
	/**
	 * 表示タグ設定>文言設定。
	 */
	wordSettings: LayoutTagGetResponse_WordSetting | null;
	/**
	 * カスタムHTML&CSS。
	 */
	customHtmlCss: LayoutTagGetResponse_CustomHtmlCss | null;
}

interface Props {
	/**
	 * マウントされるHTMLElement。
	 * CommunityTalkStoreの初期化時に使用。
	 */
	targetElement: HTMLElement;
	/**
	 * CommunityTalkStoreのInject用キー。
	 * 同一のInjectキーを使えば、同じStoreにアクセスできる。
	 * 子コンポーネントに親コンポーネントがアクセスしているStoreと同じStoreを参照させたい場合は、
	 * 子コンポーネントにこのキーを持たせること。
	 */
	communityTalkStoreKey: string;
}

export default /*#__PURE__*/_defineComponent({
  props: {
    targetElement: { type: null, required: true },
    communityTalkStoreKey: { type: String, required: true }
  },
  setup(__props: any, { expose }) {
  expose()

const props = __props as {
	/**
	 * マウントされるHTMLElement。
	 * CommunityTalkStoreの初期化時に使用。
	 */
	targetElement: HTMLElement;
	/**
	 * CommunityTalkStoreのInject用キー。
	 * 同一のInjectキーを使えば、同じStoreにアクセスできる。
	 * 子コンポーネントに親コンポーネントがアクセスしているStoreと同じStoreを参照させたい場合は、
	 * 子コンポーネントにこのキーを持たせること。
	 */
	communityTalkStoreKey: string;
}
/**
 * コミュニティトーク>トークルーム機能のルートコンポーネント。
 * 表示タグ情報の読み込み、トークルーム情報の読み込みはここで行っている。
 */



// reactiveな変数宣言
const state = reactive<State>({
	talkRoomViewModel: null,
	layoutTagGetStatus: ApiGetLoadStatus.loading,
	talkRoomGetStatus: ApiGetLoadAddStatus.beforeRequest,
	wordSettings: null,
	customHtmlCss: null,
});
const { talkRoomViewModel, layoutTagGetStatus, talkRoomGetStatus, wordSettings, customHtmlCss } = toRefs(state);

// Storeの初期化処理タグに設定された値の読み取りも行う。
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
// Storeを生成
const communityTalkStore = useCommunityTalkStore();
// Storeを親コンポーネントから供給
provide(props.communityTalkStoreKey, communityTalkStore);

const talkRoomController = new TalkRoomController();
/**
 * トークルームを取得。
 */
const getTalkRoomData = async (): Promise<void> => {
	try {
		state.talkRoomGetStatus = ApiGetLoadAddStatus.loading;
		const { viewModel, postableReviewerAttributes } = await talkRoomController.getTalkRoomData(
			communityTalkStore.getTenantId(),
			communityTalkStore.getTagCd(),
			communityTalkStore.getSubjectCd(),
			communityTalkStore.getLayoutTagData()?.generalSettings.talkRoomInitialDisplayCnt,
			communityTalkStore.getDisplayTalkRoomId(),
			communityTalkStore.getLayoutTagData()?.generalSettings.talkRoomSortType
		);
		state.talkRoomViewModel = viewModel;
		communityTalkStore.setPostableReviewerAttributes(postableReviewerAttributes);
		state.talkRoomGetStatus = ApiGetLoadAddStatus.success;
	} catch (e) {
		console.error(e);
		state.talkRoomGetStatus = ApiGetLoadAddStatus.error;
	}
};

/**
 * 初期処理。
 */
const init = async (): Promise<void> => {
	try {
		// 初期化処理&タグ情報取得
		await communityTalkStore.initAndGetLayoutTagData(props.targetElement);
		// トークルーム情報を取得
		await getTalkRoomData();
		// 文言設定、カスタムHTMLCSSを取得
		state.wordSettings = communityTalkStore.getWordSettings();
		state.customHtmlCss = communityTalkStore.getCustomHtmlCss();
		// 表示タグ情報をセットしてから、APIステータスを変える
		// こうしないと表示タグ情報が空のままレンダリングされてしまい、表示タグ情報が空であることによるエラーが発生してしまう
		state.layoutTagGetStatus = ApiGetLoadStatus.success;
		// 追加CSSの適用
		CustomCssUtil.renderCustomCss(state.customHtmlCss.cssPart);
		// DOM更新を待つ
		await nextTick();

		// ReviCoタグマネージャー等で外部定義された関数が存在すれば発火
		// eslint-disable-next-line no-undef
		if (typeof revico_communitytalk_complete === "function") {
			// eslint-disable-next-line no-undef
			revico_communitytalk_complete();
		}
	} catch (e) {
		console.error(e);
		state.layoutTagGetStatus = ApiGetLoadStatus.error;
	}
};
init();

/**
 * トークルームが1件でもあるかどうか。
 */
const isExistTalkRooms = (): boolean => {
	if (!state.talkRoomViewModel) {
		return false;
	} else if (!state.talkRoomViewModel.talkRoomCount || state.talkRoomViewModel.talkRoomCount === 0) {
		return false;
	}
	return true;
};

/**
 * APIからトークルーム情報を追加する。
 */
const addTalkRoom = async (): Promise<void> => {
	// 追加トークルームの取得
	const lastTalkRoom = state.talkRoomViewModel?.talkRooms[state.talkRoomViewModel?.talkRooms.length - 1];
	if (!lastTalkRoom) {
		console.error("lastTalkRoom cannot use. lastTalkRoom: " + lastTalkRoom);
		return;
	}

	state.talkRoomGetStatus = ApiGetLoadAddStatus.addLoading;
	try {
		const { viewModel } = await talkRoomController.getTalkRoomData(
			communityTalkStore.getTenantId(),
			communityTalkStore.getTagCd(),
			communityTalkStore.getSubjectCd(),
			communityTalkStore.getLayoutTagData()?.generalSettings.talkRoomAdditionalDisplayCnt,
			"", // トークルーム情報を追加で取得する場合は、displayTalkRoomIdを指定しない
			communityTalkStore.getLayoutTagData()?.generalSettings.talkRoomSortType,
			lastTalkRoom?.talkRoomSortKey
		);
		state.talkRoomViewModel?.talkRooms.push(...viewModel.talkRooms);
		state.talkRoomGetStatus = ApiGetLoadAddStatus.success;
	} catch (e) {
		state.talkRoomGetStatus = ApiGetLoadAddStatus.addError;
		console.error(e);
	}
};

const __returned__ = { props, state, talkRoomViewModel, layoutTagGetStatus, talkRoomGetStatus, wordSettings, customHtmlCss, communityTalkStore, talkRoomController, getTalkRoomData, init, isExistTalkRooms, addTalkRoom, ApiGetLoadAddStatus, ApiGetLoadMaster, ApiGetLoadWithAddMaster, HeaderPart, FooterPart, ReviCoLogo, CommunityTalkTitle, TalkRoomCount, TalkRoomList, TalkRoomMoreButton, NoTalkRoom, TalkRoomPost }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})